import React, { useState } from 'react';
import { Collapse, Grid, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AccountData, PolicyTypes, ReschedulePolicyData } from '../../api';
import { OrderPageTypography } from '../../orderPage/themedComponents/OrderPageTypography';
import { OrderPageButton } from '../../orderPage/themedComponents/OrderPageButton';
import { customerFacingPriceFormatter } from '../../orderPage/utils';

const cellStyle = { border: 'none', padding: 0, paddingTop: '4px' };

type RescheduleFeeTiersTableProps = {
  policy?: ReschedulePolicyData;
  account: AccountData;
  collapsible?: boolean;
};

export const RescheduleFeeTiersTable = ({ policy, account, collapsible }: RescheduleFeeTiersTableProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const feeTiers = [...(policy?.fee_tiers ?? [])].sort((first, second) => second.hours_before - first.hours_before);
  const minHoursBefore = policy?.min_hours_before ?? 0;

  if (feeTiers.length === 0 && minHoursBefore === 0) {
    return null;
  }

  const table = (
    <Grid item>
      {minHoursBefore > 0 && (
        <OrderPageTypography variant="body2">
          {t('reschedule_policy.minHours', { hours: minHoursBefore })}
        </OrderPageTypography>
      )}
      {feeTiers.length > 0 && (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ ...cellStyle, fontWeight: 600 }}>
                <OrderPageTypography variant="body2" bold>
                  {t('reschedule_policy.hoursBefore')}
                </OrderPageTypography>
              </TableCell>
              <TableCell style={{ ...cellStyle, fontWeight: 600 }}>
                <OrderPageTypography variant="body2" bold>
                  {t('reschedule_policy.fee')}
                </OrderPageTypography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {feeTiers.map((tier) => (
              <TableRow key={`rescheduleFeeTier${tier.hours_before}`}>
                <TableCell style={cellStyle}>
                  <OrderPageTypography variant="body2">{tier.hours_before}</OrderPageTypography>
                </TableCell>
                <TableCell style={cellStyle}>
                  <OrderPageTypography variant="body2">
                    {tier.policy_type === PolicyTypes.PERCENT
                      ? `${tier.value} %`
                      : customerFacingPriceFormatter(tier.value, account)}
                  </OrderPageTypography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Grid>
  );

  if (!collapsible) {
    return table;
  }

  return (
    <Grid xs={12} mt={4}>
      <OrderPageButton fullWidth variant="outlined" onClick={() => setIsOpen(!isOpen)}>
        <OrderPageTypography variant="body1"> {t('cancelation.showterms')}</OrderPageTypography>
      </OrderPageButton>
      <Collapse in={isOpen}>{table}</Collapse>
    </Grid>
  );
};
